/*
 * Coming back to the page a reader signed out from (2026-10-09).
 *
 * Entra sends a browser back after sign-out only to an address registered on the
 * app, and the one this app has registered is /admin - the sign-in redirect URI
 * (docs/ENTRA-ID-SETUP.md step 3). So sign-out goes there, and the page it began on
 * is noted in this tab first: AuthProvider.signOut writes the note, and main.tsx,
 * before anything renders, sees it on landing at /admin and goes on to that page.
 *
 * The note is the tab's own (sessionStorage), read once and removed either way,
 * only honoured on /admin itself, only for this origin, and only for a few minutes:
 * if Entra keeps the reader on its own "you have signed out" page - which it does
 * when it cannot return them - a visit to /admin hours later is not sent somewhere
 * stale.
 */

export const SIGN_OUT_RETURN_KEY = 'kb.signOutReturn';

/** Long enough for the round trip through Entra's sign-out, short enough to forget. */
export const SIGN_OUT_RETURN_TTL_MS = 10 * 60 * 1000;

/** Note the page to come back to, just before sign-out leaves for Entra. */
export function noteSignOutReturn(url: string, now: number = Date.now()): void {
  try {
    sessionStorage.setItem(SIGN_OUT_RETURN_KEY, JSON.stringify({ url, at: now }));
  } catch {
    // No storage: the reader lands on /admin, signed out, as before.
  }
}

/**
 * On landing back at /admin after sign-out, go on to the noted page. Returns true
 * when it navigated - the caller renders nothing then. Removes the note whatever
 * it decides.
 */
export function followSignOutReturn(
  navigate: (url: string) => void = (url) => window.location.replace(url),
  now: number = Date.now(),
): boolean {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(SIGN_OUT_RETURN_KEY);
    sessionStorage.removeItem(SIGN_OUT_RETURN_KEY);
  } catch {
    return false;
  }
  if (!raw || window.location.pathname !== '/admin') return false;

  try {
    const note = JSON.parse(raw) as { url?: unknown; at?: unknown };
    if (typeof note.url !== 'string' || typeof note.at !== 'number') return false;
    if (now - note.at > SIGN_OUT_RETURN_TTL_MS || now < note.at) return false;

    const target = new URL(note.url, window.location.origin);
    if (target.origin !== window.location.origin) return false;
    if (target.href === window.location.href) return false;

    navigate(target.href);
    return true;
  } catch {
    return false;
  }
}
